import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Package, Search, Check, BookOpen, Lock, Info } from 'lucide-react';
import MDEditor from '@uiw/react-md-editor';
import '@uiw/react-md-editor/markdown-editor.css';
import InlineMDInput from '../common/InlineMDInput';
import InlineMDPreview from '../common/InlineMDPreview';
import useScrollLock from '../../hooks/useScrollLock';
import { getBookCoverUrl } from '../../services/booksApi';
import { stripMarkdown } from '../../utils/markdownUtils';

const PackModal = ({ isOpen, onClose, onSave, pack, availableBooks = [], saving = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    books: [],
    deliveryFee: '',
    automaticDeliveryFee: false,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('catalog');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [failedImages, setFailedImages] = useState(new Set());

  // Lock background scroll when modal is open
  useScrollLock(isOpen);

  // Debug logging
  useEffect(() => {
    if (isOpen) {
      console.log('PackModal opened');
      console.log('Available books:', availableBooks);
      console.log('Pack data:', pack);
    }
  }, [isOpen, availableBooks, pack]);

  // Initialize form data when editing
  useEffect(() => {
    if (pack) {
      setFormData({
        name: pack.name || pack.title || '',
        description: pack.description || '',
        price: pack.price || '',
        books: pack.books || [],
        deliveryFee: pack.deliveryFee || '',
        automaticDeliveryFee: pack.automaticDeliveryFee || false,
      });
    } else {
      setFormData({
        name: '',
        description: '',
        price: '',
        books: [],
        deliveryFee: '',
        automaticDeliveryFee: false,
      });
    }
    setErrors({});
    setSearchQuery('');
    setActiveTab('catalog');
    setIsSubmitting(false);
  }, [pack, isOpen]);

  // Split books by visibleInCatalog
  const catalogBooks = React.useMemo(() => {
    if (!Array.isArray(availableBooks)) return [];
    return availableBooks.filter(b => b && b.visibleInCatalog !== false);
  }, [availableBooks]);

  const packOnlyBooks = React.useMemo(() => {
    if (!Array.isArray(availableBooks)) return [];
    return availableBooks.filter(b => b && b.visibleInCatalog === false);
  }, [availableBooks]);

  // Determine what type of books are currently selected (for homogeneity enforcement)
  const activeSelectionType = React.useMemo(() => {
    if (formData.books.length === 0) return null;
    return formData.books[0].visibleInCatalog === false ? 'pack_only' : 'catalog';
  }, [formData.books]);

  // Filter books based on search query within the active tab
  const filteredBooks = React.useMemo(() => {
    const pool = activeTab === 'catalog' ? catalogBooks : packOnlyBooks;
    const searchLower = (searchQuery || '').toLowerCase();
    if (!searchLower) return pool;
    return pool.filter(book => {
      const title = book.title || '';
      const author = book.author?.name || book.author || '';
      return title.toLowerCase().includes(searchLower) || author.toLowerCase().includes(searchLower);
    });
  }, [activeTab, catalogBooks, packOnlyBooks, searchQuery]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: '' });
  };

  const handleToggleBook = (book) => {
    const isSelected = formData.books.some(b => b.id === book.id);
    if (isSelected) {
      setFormData({
        ...formData,
        books: formData.books.filter(b => b.id !== book.id)
      });
    } else {
      setFormData({
        ...formData,
        books: [...formData.books, book]
      });
    }
    setErrors({ ...errors, books: '' });
  };

  const isBookSelected = (bookId) => {
    return formData.books.some(b => b.id === bookId);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Le nom du pack est requis';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'La description est requise';
    }

    if (!formData.price || parseFloat(formData.price) <= 0) {
      newErrors.price = 'Veuillez entrer un prix valide';
    }

    if (formData.books.length === 0) {
      newErrors.books = 'Veuillez sélectionner au moins un livre';
    } else if (formData.books.length < 2) {
      newErrors.books = 'Un pack doit contenir au moins 2 livres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare pack data
      const packData = {
        title: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        books: formData.books,
        automaticDeliveryFee: formData.automaticDeliveryFee,
        deliveryFee: formData.automaticDeliveryFee ? 0 : (parseFloat(formData.deliveryFee) || 0),
      };

      // Call onSave with pack data
      await onSave(packData);
    } catch (error) {
      console.error('Error saving pack:', error);
      setErrors({
        ...errors,
        submit: 'Une erreur est survenue lors de l\'enregistrement. Veuillez réessayer.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4"
            onClick={onClose}
          >
            <div
              className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header - Professional with green accent */}
              <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-4 py-4 sm:px-6 sm:py-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="bg-white/15 p-2 rounded-lg flex-shrink-0">
                      <Package className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <div className="min-w-0">
                      <h2 className="text-base sm:text-lg font-semibold text-white truncate">
                        {pack ? 'Modifier le Pack' : 'Nouveau Pack'}
                      </h2>
                      <p className="text-xs sm:text-sm text-green-50 hidden sm:block">
                        Créer une offre groupée de livres
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="p-2 rounded-lg hover:bg-white/15 transition-colors flex-shrink-0"
                    aria-label="Fermer"
                  >
                    <X className="w-5 h-5 text-white" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-5">
                {/* Pack Name */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Nom du Pack <span className="text-red-500">*</span>
                  </label>
                  <InlineMDInput
                    value={formData.name}
                    onChange={(value) => {
                      setFormData((prev) => ({ ...prev, name: value }));
                      if (errors.name) setErrors((prev) => ({ ...prev, name: '' }));
                    }}
                    placeholder="ex: Pack Littérature Classique"
                    error={errors.name}
                  />
                  {errors.name && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-red-600 text-xs mt-1.5 font-medium"
                    >
                      {errors.name}
                    </motion.p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <div
                    data-color-mode="light"
                    className={`rounded-lg overflow-hidden transition-all ${
                      errors.description
                        ? 'ring-2 ring-red-500 border-red-500'
                        : 'border border-gray-300 focus-within:ring-2 focus-within:ring-green-500 focus-within:border-transparent'
                    }`}
                  >
                    <MDEditor
                      value={formData.description}
                      onChange={(value) => setFormData((prev) => ({ ...prev, description: value || '' }))}
                      preview="live"
                      height={240}
                      style={{ borderRadius: 0 }}
                    />
                  </div>
                  {errors.description && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-red-600 text-xs mt-1.5 font-medium"
                    >
                      {errors.description}
                    </motion.p>
                  )}
                </div>

                {/* Price */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Prix (DZD) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    placeholder="ex: 5000"
                    min="0"
                    step="0.01"
                    className={`w-full px-4 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                      errors.price
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-green-500'
                    }`}
                  />
                  {errors.price && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-red-600 text-xs mt-1.5 font-medium"
                    >
                      {errors.price}
                    </motion.p>
                  )}
                </div>

                {/* Delivery Fee */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Frais de livraison (DZD)
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      name="deliveryFee"
                      value={formData.deliveryFee}
                      onChange={handleInputChange}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      disabled={formData.automaticDeliveryFee}
                      className={`flex-1 px-4 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 transition-all border-gray-300 focus:ring-green-500 ${
                        formData.automaticDeliveryFee ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''
                      }`}
                    />
                    <label className="flex items-center gap-2 cursor-pointer select-none whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={formData.automaticDeliveryFee}
                        onChange={(e) => setFormData({ ...formData, automaticDeliveryFee: e.target.checked })}
                        className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Auto</span>
                    </label>
                  </div>
                </div>

                {/* Book Selection */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-semibold text-gray-700">
                      Sélectionner les Livres <span className="text-red-500">*</span>
                    </label>
                    <span className="text-xs text-gray-500 font-medium">
                      {formData.books.length} livre(s) (min: 2)
                    </span>
                  </div>

                  {/* Tabs */}
                  <div className="flex gap-2 mb-3">
                    <button
                      type="button"
                      onClick={() => {
                        if (activeTab !== 'catalog') {
                          setActiveTab('catalog');
                          setFormData(prev => ({ ...prev, books: [] }));
                          setSearchQuery('');
                        }
                      }}
                      className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg border transition-all ${
                        activeTab === 'catalog'
                          ? 'bg-green-600 text-white border-green-600 shadow-sm'
                          : 'bg-white text-gray-600 border-gray-300 hover:border-green-400 hover:text-green-700'
                      }`}
                    >
                      Livres catalogue
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${activeTab === 'catalog' ? 'bg-white/20' : 'bg-gray-100'}`}>
                        {catalogBooks.length}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (activeTab !== 'pack_only') {
                          setActiveTab('pack_only');
                          setFormData(prev => ({ ...prev, books: [] }));
                          setSearchQuery('');
                        }
                      }}
                      className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg border transition-all ${
                        activeTab === 'pack_only'
                          ? 'bg-gray-700 text-white border-gray-700 shadow-sm'
                          : 'bg-white text-gray-600 border-gray-300 hover:border-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Livres pack uniquement
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${activeTab === 'pack_only' ? 'bg-white/20' : 'bg-gray-100'}`}>
                        {packOnlyBooks.length}
                      </span>
                    </button>
                  </div>

                  {/* Info note for pack-only tab */}
                  {activeTab === 'pack_only' && (
                    <div className="flex items-start gap-2 mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                      <Info className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-700">
                        Ce pack affichera uniquement le prix total — sans prix barrés ni pourcentage de réduction.
                      </p>
                    </div>
                  )}

                  {/* Search Bar */}
                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Rechercher par titre ou auteur..."
                      className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  {/* Books Grid */}
                  <div className="border border-gray-300 rounded-lg p-3 sm:p-4 bg-gray-50 max-h-64 overflow-y-auto">
                    {filteredBooks.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <BookOpen className="w-10 h-10 mx-auto mb-2 text-gray-400" />
                        <p className="text-sm">Aucun livre trouvé</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {filteredBooks.map((book) => {
                          const selected = isBookSelected(book.id);
                          const tabType = activeTab === 'catalog' ? 'catalog' : 'pack_only';
                          const isLocked = activeSelectionType !== null && activeSelectionType !== tabType && !selected;
                          return (
                            <div
                              key={book.id}
                              onClick={() => !isLocked && handleToggleBook(book)}
                              className={`relative rounded-lg border transition-all duration-200 ${
                                isLocked
                                  ? 'opacity-40 cursor-not-allowed'
                                  : selected
                                    ? 'border-green-500 bg-green-50 shadow-sm cursor-pointer'
                                    : 'border-gray-200 bg-white hover:border-green-300 hover:shadow-sm cursor-pointer'
                              }`}
                            >
                              {/* Lock overlay */}
                              {isLocked && (
                                <div className="absolute inset-0 flex items-center justify-center z-10 rounded-lg">
                                  <Lock className="w-5 h-5 text-gray-500" />
                                </div>
                              )}

                              {/* Selection Indicator */}
                              {selected && !isLocked && (
                                <div className="absolute top-1.5 right-1.5 bg-green-600 text-white rounded-full p-0.5 z-10 shadow-sm">
                                  <Check className="w-3.5 h-3.5" />
                                </div>
                              )}

                              {/* Book Card */}
                              {failedImages.has(book.id) ? (
                                <div className="w-full h-28 bg-gradient-to-br from-gray-100 to-gray-200 rounded-t-lg flex items-center justify-center">
                                  <BookOpen className="w-7 h-7 text-gray-400" />
                                </div>
                              ) : (
                                <img
                                  src={getBookCoverUrl(book.id, failedImages.has(`${book.id}-placeholder`))}
                                  alt={book.title}
                                  className="w-full h-28 object-cover rounded-t-lg"
                                  onError={(e) => {
                                    if (!failedImages.has(`${book.id}-placeholder`)) {
                                      setFailedImages(prev => new Set(prev).add(`${book.id}-placeholder`));
                                      e.target.src = getBookCoverUrl(book.id, true);
                                    } else {
                                      setFailedImages(prev => new Set(prev).add(book.id));
                                    }
                                  }}
                                />
                              )}
                              <div className="p-2.5">
                                <h4 className="font-semibold text-xs text-gray-800 line-clamp-1 mb-0.5" title={stripMarkdown(book.title)}>
                                  <InlineMDPreview>{book.title}</InlineMDPreview>
                                </h4>
                                <p className="text-xs text-gray-500 truncate mb-1" title={book.author?.name || book.author || 'Auteur inconnu'}>
                                  {book.author?.name || book.author || 'Auteur inconnu'}
                                </p>
                                <div className="flex items-center justify-between gap-1">
                                  <span className="inline-block px-1.5 py-0.5 text-xs font-medium text-blue-700 bg-blue-100 rounded">
                                    {book.language || 'N/A'}
                                  </span>
                                  <span className="text-xs font-semibold text-green-600">
                                    {book.price} DZD
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {errors.books && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-red-600 text-xs mt-1.5 font-medium"
                    >
                      {errors.books}
                    </motion.p>
                  )}
                </div>

                {/* Selected Books Preview */}
                {formData.books.length > 0 && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2.5">
                      Livres Sélectionnés
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {formData.books.map((book) => (
                        <div
                          key={book.id}
                          className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-800 px-2.5 py-1.5 rounded-lg text-xs"
                        >
                          <span className="line-clamp-1 max-w-[120px] sm:max-w-[160px] font-medium">
                            <InlineMDPreview>{book.title}</InlineMDPreview>
                          </span>
                          <button
                            onClick={() => handleToggleBook(book)}
                            className="hover:bg-green-100 rounded-full p-0.5 transition-colors"
                            aria-label={`Retirer ${stripMarkdown(book.title)}`}
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex flex-col gap-2.5 px-4 py-4 sm:px-6 border-t bg-gray-50">
                {/* Error Message */}
                {errors.submit && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-red-50 border border-red-200 text-red-700 px-3 py-2.5 rounded-lg text-xs"
                  >
                    {errors.submit}
                  </motion.div>
                )}

                {/* Buttons */}
                <div className="flex justify-end gap-2.5">
                  <button
                    onClick={onClose}
                    disabled={isSubmitting || saving}
                    className="px-5 py-2.5 text-sm border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting || saving}
                    className="px-5 py-2.5 text-sm bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {(isSubmitting || saving) && (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    )}
                    {pack ? 'Mettre à Jour' : 'Créer le Pack'}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default PackModal;
